/**
 * What the create screen's remaining lists offer.
 *
 * Constants rather than fetches: before-or-after is a pair, a clock is a
 * clock, and a week is a week — none of the three is a thing a practice adds
 * to, and no endpoint offers them.
 *
 * Two lists used to be here and are not any more. The units are
 * `/api/v1/supplement-units/` and the meals are `/api/v1/meal-slot-types/`,
 * read through `useSupplementUnits` and `useMealSlotTypes`, because a write
 * sends the catalog row's id for each rather than the word — and a word this
 * file invented would be one the backend has never heard of.
 */

/** Before the meal, or after it. */
export const WHEN_OPTIONS: string[] = ["Before", "After"];

/**
 * The days, in the order the toggles are drawn — Monday first, the way a week
 * is read here, and the way the table writes them back out.
 */
export const DAY_LABELS: string[] = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
];

/**
 * What each day is called on a toggle and on a frequency chip.
 *
 * Two letters rather than one: a row of single initials has "T" twice and "S"
 * twice in it, and a reader picking days out of it has to count along from
 * Monday. The controls still carry the full name as their accessible label,
 * since "Mo" read aloud is not a day either.
 */
/**
 * The whole name, which is what every one of these controls is called to a
 * screen reader. Neither "Mo" nor "Mon" is a day read aloud.
 */
export const DAY_FULL: string[] = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const DAY_SHORT: string[] = [
  "Mo",
  "Tu",
  "We",
  "Th",
  "Fr",
  "Sa",
  "Su",
];

/**
 * Every half hour of the day, written the way the design writes one.
 *
 * Built rather than listed: forty-eight lines of "07:30 am" is a page of
 * constants nobody can check, and the loop is the same shape as the label.
 */
export const TIME_OPTIONS: string[] = Array.from({ length: 48 }, (_, index) => {
  const hour24 = Math.floor(index / 2);
  const minute = index % 2 === 0 ? "00" : "30";
  // Midnight and noon are both 12 in a twelve-hour clock, which is the one
  // case a plain modulo gets wrong.
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const period = hour24 < 12 ? "am" : "pm";

  return `${String(hour12).padStart(2, "0")}:${minute} ${period}`;
});

/**
 * The same clock, taken apart: what the Time column's three lists offer, now
 * that a time is picked as an hour, a minute and a half of the day rather
 * than scrolled for in a list of forty-eight.
 *
 * The minutes go by five rather than by thirty — three lists are what makes
 * a finer clock readable, and a dose at twenty past is not a thing the old
 * list could say. `TIME_OPTIONS` stays for the label a card starts on and for
 * anything else that wants the whole clock at once.
 */
export const HOUR_OPTIONS: string[] = Array.from({ length: 12 }, (_, index) =>
  String(index + 1).padStart(2, "0")
);

export const MINUTE_OPTIONS: string[] = Array.from({ length: 12 }, (_, index) =>
  String(index * 5).padStart(2, "0")
);

export const PERIOD_OPTIONS: string[] = ["am", "pm"];

/** The three parts of a `"08:00 am"` label, as the Time column picks them. */
export interface ClockParts {
  hour: string;
  minute: string;
  period: string;
}

/**
 * `"08:00 am"` taken apart.
 *
 * Tolerant of holes, because a dose being written has them: the card holds
 * the one label while its three lists are picked from one at a time, and
 * between picks it reads `"09:"`, `":30"` or `": pm"` — whichever parts have
 * been chosen, in their places, and nothing standing in for the rest. The
 * lists then show exactly what was picked and a placeholder where nothing
 * was, rather than an hour or a half of the day the reader never chose.
 *
 * A label that is none of these — empty, or something a fetched plan wrote
 * that is not a clock time at all — comes back as three blanks.
 */
export const toClockParts = (label: string): ClockParts => {
  const match = /^(\d{1,2})?:(\d{2})?\s*(am|pm)?$/i.exec(label.trim());
  if (!match) return { hour: "", minute: "", period: "" };

  const [, hour = "", minute = "", period = ""] = match;
  return {
    hour: hour && hour.padStart(2, "0"),
    minute,
    period: period.toLowerCase(),
  };
};

/**
 * The inverse: three parts as the one label the card holds, holes and all —
 * see `toClockParts`. Whole once all three are picked, and `"08:00 am"` then
 * reads exactly as `TIME_OPTIONS` writes it.
 */
export const toClockLabelFromParts = ({ hour, minute, period }: ClockParts): string =>
  `${hour}:${minute} ${period}`.trim();

/** Whether a label is a whole time — all three parts picked. */
export const isWholeClockLabel = (label: string): boolean =>
  /^\d{1,2}:\d{2}\s*(am|pm)$/i.test(label.trim());
